// src/pages/InboundLeads.jsx
// Main inbox for all leads coming from Meta, Google, IndiaMart etc.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LeadIntake } from '../services/api';
import { useAuth, ROLES } from '../context/AuthContext';
import LeadDetailModal  from '../components/LeadDetailModal';
import CSVImportModal   from '../components/CSVImportModal';

const STATUS_STYLES = {
    new:       { label: 'New',       bg: '#dbeafe', color: '#1e40af' },
    converted: { label: 'Converted', bg: '#dcfce7', color: '#166534' },
    duplicate: { label: 'Duplicate', bg: '#fef3c7', color: '#92400e' },
    junk:      { label: 'Junk',      bg: '#f3f4f6', color: '#6b7280' },
};

const SOURCE_ICONS = {
    meta:       '📘',
    google:     '🔍',
    indiamart:  '🏭',
    tradeindia: '🤝',
    justdial:   '📞',
    manual:     '📋',
    custom:     '🔗',
};

export default function InboundLeads() {
    const { user } = useAuth();

    const [leads,      setLeads]      = useState([]);
    const [sources,    setSources]    = useState([]);
    const [busy,       setBusy]       = useState(true);
    const [selected,   setSelected]   = useState(null);   // lead for detail modal
    const [showCSV,    setShowCSV]    = useState(false);
    const [err,        setErr]        = useState('');
    const [success,    setSuccess]    = useState('');

    // Filters
    const [filterStatus, setFilterStatus] = useState('new');
    const [filterSource, setFilterSource] = useState('');
    const [filterFrom,   setFilterFrom]   = useState('');
    const [filterTo,     setFilterTo]     = useState('');
    const [search,       setSearch]       = useState('');

    const searchTimer = useRef(null);

    // ── Load ──────────────────────────────────────────────────
    const load = useCallback(() => {
        setBusy(true);
        const filters = {
            status:    filterStatus,
            source_id: filterSource,
            from:      filterFrom,
            to:        filterTo,
            search,
        };
        Promise.all([LeadIntake.list(filters), LeadIntake.sources()])
            .then(([l, s]) => { setLeads(l); setSources(s); })
            .catch(e => setErr(e.message))
            .finally(() => setBusy(false));
    }, [filterStatus, filterSource, filterFrom, filterTo, search]);

    useEffect(load, [load]);

    // Debounce search
    const handleSearch = (val) => {
        setSearch(val);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(load, 500);
    };

    // ── Convert lead → customer ───────────────────────────────
    const handleConvert = async (lead) => {
        try {
            const res = await LeadIntake.convert(lead.id);
            if (res.error) { setErr(res.error); return; }
            const msg = res.existing
                ? `Matched to existing customer #${res.customer_id}`
                : `New customer #${res.customer_id} created — unassigned`;
            setSuccess(msg);
            setSelected(null);
            load();
            setTimeout(() => setSuccess(''), 5000);
        } catch (ex) { setErr(ex.message); }
    };

    // ── Mark junk ─────────────────────────────────────────────
    const handleJunk = async (lead, markAs = 'junk') => {
        if (!window.confirm(`Mark this lead as ${markAs}?`)) return;
        try {
            await LeadIntake.markJunk(lead.id, markAs);
            setSelected(null);
            load();
        } catch (ex) { setErr(ex.message); }
    };

    // ── Tab counts ────────────────────────────────────────────
    const allLeads    = leads;
    const statusCount = (s) => allLeads.filter(l => l.status === s).length;

    return (
        <div className="page">
            {/* ── Header ── */}
            <div className="page-header">
                <div className="page-title">📥 Inbound Leads</div>
                <input
                    className="search-bar"
                    placeholder="Search name, phone, email…"
                    onChange={e => handleSearch(e.target.value)}
                />
                <button className="btn btn-ghost btn-sm"
                    onClick={() => setShowCSV(true)}>
                    ⬆ Import CSV
                </button>
            </div>

            {/* ── Alerts ── */}
            {err && (
                <div className="alert alert-error" style={{ marginBottom:'1rem' }}>
                    {err}
                    <button onClick={() => setErr('')}
                        style={{ marginLeft:8, background:'none', border:'none', cursor:'pointer', fontWeight:700 }}>✕</button>
                </div>
            )}
            {success && (
                <div style={{ background:'#dcfce7', color:'#166534', border:'1px solid #86efac', borderRadius:8, padding:'9px 14px', marginBottom:'1rem', fontSize:'.875rem' }}>
                    ✓ {success}
                </div>
            )}

            {/* ── Status tabs ── */}
            <div className="tabs" style={{ marginBottom:'1rem' }}>
                {Object.entries(STATUS_STYLES).map(([k, v]) => (
                    <button key={k}
                        className={`tab${filterStatus === k ? ' active' : ''}`}
                        onClick={() => setFilterStatus(k)}>
                        {v.label}
                        <span style={{
                            marginLeft:5, fontSize:'.7rem', fontWeight:700,
                            background: filterStatus===k ? '#fff3' : v.bg,
                            color: filterStatus===k ? '#fff' : v.color,
                            borderRadius:10, padding:'1px 7px',
                        }}>
                            {statusCount(k)}
                        </span>
                    </button>
                ))}
                <button className={`tab${filterStatus==='' ? ' active':''}`}
                    onClick={() => setFilterStatus('')}>
                    All <span style={{ marginLeft:5, fontSize:'.7rem', fontWeight:700,
                        background:filterStatus===''?'#fff3':'#f3f4f6', color:filterStatus===''?'#fff':'#374151',
                        borderRadius:10, padding:'1px 7px' }}>{leads.length}</span>
                </button>
            </div>

            {/* ── Filters row ── */}
            <div style={{ display:'flex', gap:'.75rem', marginBottom:'1rem', flexWrap:'wrap', alignItems:'center' }}>
                <select
                    className="search-bar"
                    style={{ maxWidth:180 }}
                    value={filterSource}
                    onChange={e => setFilterSource(e.target.value)}
                >
                    <option value="">All Sources</option>
                    {sources.map(s => (
                        <option key={s.id} value={s.id}>
                            {SOURCE_ICONS[s.slug] || '📌'} {s.name}
                        </option>
                    ))}
                </select>

                <div style={{ display:'flex', alignItems:'center', gap:'.4rem', fontSize:'.8rem', color:'var(--muted)' }}>
                    From
                    <input type="date" value={filterFrom}
                        onChange={e => setFilterFrom(e.target.value)}
                        style={{ fontSize:'.8rem', padding:'4px 8px', borderRadius:7, border:'1px solid var(--border)', background:'var(--bg)' }} />
                    To
                    <input type="date" value={filterTo}
                        onChange={e => setFilterTo(e.target.value)}
                        style={{ fontSize:'.8rem', padding:'4px 8px', borderRadius:7, border:'1px solid var(--border)', background:'var(--bg)' }} />
                    {(filterFrom || filterTo) && (
                        <button onClick={() => { setFilterFrom(''); setFilterTo(''); }}
                            style={{ background:'none', border:'none', cursor:'pointer', color:'var(--muted)' }}>✕</button>
                    )}
                </div>
            </div>

            {/* ── Leads table ── */}
            {busy ? (
                <div className="spinner-wrap"><div className="spinner" /></div>
            ) : leads.length === 0 ? (
                <div className="empty">
                    No {filterStatus} leads found
                    {filterStatus === 'new' && (
                        <div style={{ marginTop:'1rem', fontSize:'.85rem', color:'var(--muted)' }}>
                            Configure webhook URLs in the <strong>Sources</strong> tab to start receiving leads.
                        </div>
                    )}
                </div>
            ) : (
                <div className="tbl-wrap">
                    <table className="tbl">
                        <thead>
                            <tr>
                                <th>Source</th>
                                <th>Name</th>
                                <th>Phone</th>
                                <th>Email</th>
                                <th>Company</th>
                                <th>Product Interest</th>
                                <th>Received</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leads.map(lead => {
                                const st = STATUS_STYLES[lead.status] || STATUS_STYLES.new;
                                const icon = SOURCE_ICONS[lead.source_slug] || '📌';
                                return (
                                    <tr key={lead.id}
                                        style={{ opacity: lead.status === 'junk' ? 0.5 : 1 }}>

                                        <td>
                                            <span title={lead.source_name}
                                                style={{ fontSize:'1.1rem' }}>{icon}</span>
                                            <span style={{ fontSize:'.72rem', color:'var(--muted)', display:'block' }}>
                                                {lead.source_name}
                                            </span>
                                        </td>

                                        <td>
                                            <span style={{ fontWeight:600, cursor:'pointer', color:'var(--accent)' }}
                                                onClick={() => setSelected(lead)}>
                                                {lead.full_name || '—'}
                                            </span>
                                            {lead.city && (
                                                <span style={{ fontSize:'.72rem', color:'var(--muted)', display:'block' }}>
                                                    📍 {lead.city}
                                                </span>
                                            )}
                                        </td>

                                        <td>
                                            <a href={`tel:${lead.phone}`}
                                                style={{ color:'var(--text)', textDecoration:'none', fontSize:'.88rem' }}>
                                                {lead.phone || '—'}
                                            </a>
                                        </td>

                                        <td style={{ fontSize:'.82rem' }}>{lead.email || '—'}</td>

                                        <td style={{ fontSize:'.85rem' }}>{lead.company || '—'}</td>

                                        <td style={{ fontSize:'.8rem', color:'var(--muted)', maxWidth:160 }}>
                                            <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                                {lead.product_interest || '—'}
                                            </div>
                                        </td>

                                        <td style={{ fontSize:'.75rem', color:'var(--muted)' }}>
                                            {new Date(lead.created_at).toLocaleDateString('en-IN', {
                                                day:'2-digit', month:'short', year:'2-digit',
                                            })}
                                            <span style={{ display:'block' }}>
                                                {new Date(lead.created_at).toLocaleTimeString('en-IN', {
                                                    hour:'2-digit', minute:'2-digit',
                                                })}
                                            </span>
                                        </td>

                                        <td>
                                            <span style={{
                                                background:st.bg, color:st.color,
                                                border:`1px solid ${st.color}40`,
                                                padding:'2px 9px', borderRadius:12,
                                                fontSize:'.72rem', fontWeight:700,
                                            }}>
                                                {st.label}
                                            </span>
                                            {lead.status === 'converted' && lead.customer_id > 0 && (
                                                <span style={{ display:'block', fontSize:'.68rem', color:'var(--muted)', marginTop:2 }}>
                                                    Cust #{lead.customer_id}
                                                </span>
                                            )}
                                        </td>

                                        <td>
                                            <div style={{ display:'flex', gap:'.3rem', flexWrap:'wrap' }}>
                                                {/* View details */}
                                                <button className="btn btn-sm btn-ghost"
                                                    onClick={() => setSelected(lead)}>
                                                    View
                                                </button>

                                                {/* Convert */}
                                                {lead.status === 'new' && (
                                                    <button className="btn btn-sm btn-primary"
                                                        style={{ fontSize:'.75rem' }}
                                                        onClick={() => handleConvert(lead)}>
                                                        → Customer
                                                    </button>
                                                )}

                                                {/* Junk */}
                                                {lead.status === 'new' && (
                                                    <button className="btn btn-sm"
                                                        style={{ background:'#f3f4f6', color:'#6b7280',
                                                            border:'1px solid #e5e7eb', fontSize:'.72rem' }}
                                                        onClick={() => handleJunk(lead, 'junk')}>
                                                        Junk
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── Detail modal ── */}
            {selected && (
                <LeadDetailModal
                    lead={selected}
                    onClose={() => setSelected(null)}
                    onConvert={() => handleConvert(selected)}
                    onJunk={(markAs) => handleJunk(selected, markAs)}
                />
            )}

            {/* ── CSV import modal ── */}
            {showCSV && (
                <CSVImportModal
                    sources={sources}
                    onClose={() => setShowCSV(false)}
                    onImported={() => { setShowCSV(false); load(); }}
                />
            )}
        </div>
    );
}
